/** Mise en forme des coordonnées des laboratoires (sans dépendance serveur : utilisable côté client). */
import { isMappable } from "@/lib/labs/geo";
import { normalizeMarPhone } from "@/lib/validation/phone-format";

type Loc = "fr" | "ar" | "en";
const asLoc = (l: string): Loc => (l === "ar" || l === "en" ? l : "fr");

/** Chiffres d'un numéro au format national marocain (préfixes 00212 / +212 / 212 → 0). */
function nationalDigits(phone: string): string {
  return phone.replace(/\D/g, "").replace(/^00/, "").replace(/^212/, "0");
}

/**
 * Numéro factice : « 05 22 00 00 00 » (192 fiches Dabadoc au 30/09/2026), chiffres
 * répétés, numéro trop court. Même règle que le JSON-LD des établissements
 * (lib/seo/establishment-meta.ts) et que le nettoyage du registre
 * (scripts/labs/apply-lab-registry.ts), plus « six zéros finaux ».
 */
export function isPlaceholderPhone(phone: string | null | undefined): boolean {
  if (!phone) return false;
  const d = nationalDigits(phone);
  return d.length < 9 || /^0?5?2?2?0+$/.test(d) || /^0?(\d)\1{7,}$/.test(d) || /0{6}$/.test(d);
}

/** « 0522236717 » → « 05 22 23 67 17 » ; tout autre format est rendu tel quel. */
export function displayPhone(phone: string): string {
  const d = nationalDigits(phone);
  return /^0\d{9}$/.test(d) ? d.replace(/(\d{2})(?=\d)/g, "$1 ").trim() : phone;
}

/** Lien tel: au format international. */
export function telHref(phone: string): string {
  const d = nationalDigits(phone);
  return /^0\d{9}$/.test(d) ? `tel:+212${d.slice(1)}` : `tel:${phone.replace(/[^\d+]/g, "")}`;
}

/**
 * Lien wa.me pour un numéro WhatsApp DÉCLARÉ (champ Clinic.whatsapp), fixe ou
 * mobile. Jamais appelé avec le téléphone : voir labWhatsappHref.
 */
export function whatsappHref(declared: string | null | undefined): string | null {
  if (!declared || isPlaceholderPhone(declared)) return null;
  const d = nationalDigits(declared);
  return /^0[5-8]\d{8}$/.test(d) ? `https://wa.me/212${d.slice(1)}` : null;
}

/**
 * Bouton WhatsApp d'un laboratoire : UNIQUEMENT depuis le numéro WhatsApp
 * déclaré, jamais déduit du téléphone — un 06/07 publié est souvent le mobile
 * personnel du biologiste (revue sécurité du 30/09/2026). `phone` est ignoré
 * volontairement.
 */
export function labWhatsappHref(lab: { whatsapp: string | null; phone?: string | null }): string | null {
  return whatsappHref(lab.whatsapp);
}

/**
 * Itinéraire Google Maps : coordonnées seulement si elles sont cartographiables
 * (precise/street, cf. isMappable), sinon l'adresse. `geoPrecision` absent =
 * coordonnées déjà filtrées par l'appelant (LabCardData).
 */
export function directionsHref(
  lab: { lat: number | null; lng: number | null; addressLine1: string; name: string; geoPrecision?: string | null },
  cityName: string,
): string {
  const useCoords = lab.lat != null && lab.lng != null && (lab.geoPrecision === undefined || isMappable(lab.geoPrecision));
  const dest = useCoords ? `${lab.lat},${lab.lng}` : [lab.name, lab.addressLine1, cityName, "Maroc"].filter(Boolean).join(", ");
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;
}

const DISTANCE_UNITS: Record<Loc, { km: string; m: string; intl: string }> = {
  fr: { km: "km", m: "m", intl: "fr-FR" },
  ar: { km: "كلم", m: "م", intl: "ar-MA" },
  en: { km: "km", m: "m", intl: "en-GB" },
};

/** Distance lisible : « 850 m », « 2,4 km », « 2,4 كلم ». */
export function displayDistance(km: number, locale: string): string {
  const u = DISTANCE_UNITS[asLoc(locale)];
  if (km < 1) return `${Math.max(50, Math.round((km * 1000) / 50) * 50)} ${u.m}`;
  return `${km.toLocaleString(u.intl, { maximumFractionDigits: 1 })} ${u.km}`;
}

/** Fiche au nom d'une personne (« Dr Prénom Nom ») : c'est le biologiste lui-même. */
export function isPersonName(name: string): boolean {
  return /^(dr|pr|docteur|professeur)\.?\s/i.test(name.trim());
}

/** Laboratoire d'anatomie pathologique (Clinic.specialties, posé par scripts/labs/apply-lab-registry.ts). */
export function isAnapath(specialties: readonly string[] | null | undefined): boolean {
  return !!specialties?.includes("anatomie-pathologique");
}

const CONVENTION_LABELS: Record<string, Record<Loc, string>> = {
  mutuelles: { fr: "Mutuelles", ar: "التعاضديات", en: "Mutual insurers" },
  assurances: { fr: "Assurances privées", ar: "التأمينات الخاصة", en: "Private insurers" },
};

/** Libellé d'une convention déclarée ; les sigles (CNOPS, CNSS, FAR…) restent tels quels. */
export function conventionLabel(code: string, locale: string): string {
  return CONVENTION_LABELS[code.trim().toLowerCase()]?.[asLoc(locale)] ?? code;
}

/** Séparateur de liste : virgule arabe en AR. */
export function listSeparator(locale: string): string {
  return asLoc(locale) === "ar" ? "، " : ", ";
}

/**
 * Même contrôle que phoneMar (lib/validation/phone.ts), sans importer zod dans
 * le bundle client : délègue à normalizeMarPhone (phone-format.ts, sans
 * dépendance) au lieu d'en recopier le motif — depuis le 21/10/2026 elle
 * accepte aussi 00212…, 212…, « +212 0… » et les chiffres arabes-indiens ; une
 * copie figée aurait refusé côté navigateur ce que le serveur accepte.
 */
export function isValidMarPhone(raw: string): boolean {
  return normalizeMarPhone(raw) !== null;
}
